import React from "react";
import { KIND_COLORS_2D, placeRoom, placeDoor } from "./buildingHelpers";
import { useStorefrontOverrides } from "@/hooks/useStorefrontOverrides";

/**
 * Top-down architectural floor plan rendered in SVG.
 * Now wing-aware: lays out all wings of the building in their site positions and
 * draws each wing's units at the active floor level.
 * For commercial plaza (single-story), level 1 is always shown.
 */
const SCALE = 2.4; // px per ft — coarser than before because U is ~150ft wide × ~408ft tall

export default function FloorPlan2D({ building, activeFloor, showEgress, showBalconies }) {
  const stores = useStorefrontOverrides();
  const margin = 60;
  const siteW = (building.width_ft + 40) * SCALE + margin * 2;
  const siteH = (building.depth_ft + 40) * SCALE + margin * 2;

  const f2 = (n) => n * SCALE;
  const tx = (n) => margin + 20 + f2(n);
  // Z grows toward NORTH (up on screen) — flip so larger z = smaller screen y
  const tz = (n) => margin + 20 + f2((building.depth_ft + 10) - n);

  // Rotate (rx, rz) by deg around origin (0, 0), then translate to (ox, oz)
  function transformWingPoint(wing, lx, lz) {
    const r = (wing.rotation_deg || 0) * Math.PI / 180;
    const cos = Math.cos(r), sin = Math.sin(r);
    const wx = lx * cos - lz * sin + wing.origin_x;
    const wz = lx * sin + lz * cos + wing.origin_z;
    return [wx, wz];
  }

  const unitTypes = Object.fromEntries(building.unit_types.map((u) => [u.id, u]));
  const wings = building.wings;

  return (
    <div data-testid="floor-plan-2d" className="w-full overflow-auto bg-[#070b10] p-4">
      <svg width={siteW} height={siteH} viewBox={`0 0 ${siteW} ${siteH}`} className="block">
        <defs>
          <pattern id="grid-1ft" width={SCALE} height={SCALE} patternUnits="userSpaceOnUse">
            <path d={`M ${SCALE} 0 L 0 0 0 ${SCALE}`} fill="none" stroke="#0f1a2a" strokeWidth="0.4" />
          </pattern>
          <pattern id="grid-5ft" width={SCALE * 5} height={SCALE * 5} patternUnits="userSpaceOnUse">
            <rect width={SCALE * 5} height={SCALE * 5} fill="url(#grid-1ft)" />
            <path d={`M ${SCALE * 5} 0 L 0 0 0 ${SCALE * 5}`} fill="none" stroke="#1e293b" strokeWidth="0.8" />
          </pattern>
          <marker id="egress-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
          </marker>
        </defs>
        <rect width={siteW} height={siteH} fill="#070b10" />
        <rect x={margin} y={margin} width={siteW - margin * 2} height={siteH - margin * 2} fill="url(#grid-5ft)" />

        {/* Title block */}
        <text x={margin} y={28} fill="#facc15" fontFamily="monospace" fontSize="11" letterSpacing="3">
          {`LEVEL ${activeFloor} · ${building.name}`}
        </text>
        <text x={siteW - margin - 4} y={28} textAnchor="end" fill="#5b6776" fontFamily="monospace" fontSize="10" letterSpacing="2">
          {`SCALE 1" = 30'-0"`}
        </text>

        {/* Compass */}
        <g transform={`translate(${siteW - 50}, 50)`}>
          <circle r="18" fill="#0b0f14" stroke="#facc15" strokeWidth="1.2" />
          <polygon points="0,-13 5,3 0,0 -5,3" fill="#facc15" />
          <text y="-20" textAnchor="middle" fill="#facc15" fontSize="10" letterSpacing="3" fontFamily="monospace" fontWeight="700">N</text>
        </g>

        {wings.map((wing) => {
          // Only render this wing if it actually has the active floor.
          // (All residential wings are 3 floors; commercial is 1 floor.)
          const floor = wing.floors.find((f) => f.level === activeFloor);
          if (!floor) return null;

          // Wing envelope rectangle (in local coords)
          const wingLen = wing.length_ft;
          const wingDep = wing.depth_ft;
          // Outline corners in local: (0,0), (L,0), (L,D), (0,D)
          const corners = [
            transformWingPoint(wing, 0, 0),
            transformWingPoint(wing, wingLen, 0),
            transformWingPoint(wing, wingLen, wingDep),
            transformWingPoint(wing, 0, wingDep),
          ];
          const polyPts = corners.map(([wx, wz]) => `${tx(wx)},${tz(wz)}`).join(" ");

          // Wing label position (centered)
          const [labelX, labelZ] = transformWingPoint(wing, wingLen / 2, wingDep / 2);

          // Stairwells (in local coords)
          return (
            <g key={wing.id} data-testid={`wing-${wing.id}`}>
              {/* Wing outline */}
              <polygon points={polyPts} fill="none" stroke="#facc15" strokeWidth="1.2" strokeDasharray="3 2" opacity="0.6" />

              {/* Stairwells */}
              {wing.stairwells.map((s) => {
                const sc = [
                  transformWingPoint(wing, s.x, s.z),
                  transformWingPoint(wing, s.x + s.w, s.z),
                  transformWingPoint(wing, s.x + s.w, s.z + s.d),
                  transformWingPoint(wing, s.x, s.z + s.d),
                ];
                const sPoly = sc.map(([wx, wz]) => `${tx(wx)},${tz(wz)}`).join(" ");
                const [scx, scz] = transformWingPoint(wing, s.x + s.w / 2, s.z + s.d / 2);
                return (
                  <g key={s.id}>
                    <polygon points={sPoly} fill="#1f2937" stroke="#facc15" strokeWidth="1.2" />
                    <text x={tx(scx)} y={tz(scz)} textAnchor="middle"
                          fill="#facc15" fontSize="6" letterSpacing="1" fontFamily="monospace">
                      {s.label}
                    </text>
                  </g>
                );
              })}

              {/* Units */}
              {floor.units.map((u, idx) => {
                const ut = unitTypes[u.unit_type_id];
                if (!ut) return null;

                // For storefronts, swap label + color with the user's overrides
                const isStore = ut.id === "COMM_40";
                const storeIdx = isStore ? Math.round(u.x / 32.0) : -1;
                const override = isStore && storeIdx >= 0 && storeIdx < stores.length ? stores[storeIdx] : null;
                const unitLabel = override ? override.name : u.label;
                const unitColor = override ? override.color : ut.color;

                // Unit rectangle in local coords (x to x+L, z to z+D)
                const uc = [
                  transformWingPoint(wing, u.x, u.z),
                  transformWingPoint(wing, u.x + ut.length_ft, u.z),
                  transformWingPoint(wing, u.x + ut.length_ft, u.z + ut.depth_ft),
                  transformWingPoint(wing, u.x, u.z + ut.depth_ft),
                ];
                const uPoly = uc.map(([wx, wz]) => `${tx(wx)},${tz(wz)}`).join(" ");
                const [ucx, ucz] = transformWingPoint(wing, u.x + ut.length_ft / 2, u.z + ut.depth_ft / 2);

                return (
                  <g key={`${wing.id}-${idx}`} data-testid={`plan-unit-${unitLabel}`}>
                    {/* Container shell — 2BR has higher opacity so it stands out */}
                    <polygon
                      points={uPoly}
                      fill={unitColor}
                      fillOpacity={ut.id === "TWOBR_40" ? "0.42" : isStore ? "0.55" : "0.18"}
                      stroke={unitColor}
                      strokeWidth={ut.id === "TWOBR_40" ? "1.8" : "1.2"}
                    />

                    {/* Rooms (split bath/kitchen/etc.) */}
                    {ut.rooms.map((room, ri) => {
                      const r = placeRoom(u, ut, room);
                      const rc = [
                        transformWingPoint(wing, r.x, r.z),
                        transformWingPoint(wing, r.x + r.w, r.z),
                        transformWingPoint(wing, r.x + r.w, r.z + r.d),
                        transformWingPoint(wing, r.x, r.z + r.d),
                      ];
                      const rPoly = rc.map(([wx, wz]) => `${tx(wx)},${tz(wz)}`).join(" ");
                      const [rcx, rcz] = transformWingPoint(wing, r.x + r.w / 2, r.z + r.d / 2);
                      return (
                        <g key={`room-${room.kind}-${room.name}-${ri}`}>
                          <polygon points={rPoly} fill={KIND_COLORS_2D[room.kind]} fillOpacity="0.85"
                                   stroke="#e6edf3" strokeWidth="0.3" />
                          {r.w > 4 && r.d > 2.5 && (
                            <text x={tx(rcx)} y={tz(rcz) - 1}
                                  textAnchor="middle" fill="#e6edf3" fontSize="3.2"
                                  fontFamily="monospace" letterSpacing="0.5">
                              {room.name}
                            </text>
                          )}
                          {showEgress && room.egress_window && (() => {
                            // Place red marker on the outer-most wall of the unit (typically z=0 side)
                            const onSide0 = (room.y) <= 0.01;
                            const [ex1, ez1] = transformWingPoint(wing,
                              r.x + 0.5, onSide0 ? u.z : u.z + ut.depth_ft);
                            const [ex2, ez2] = transformWingPoint(wing,
                              r.x + r.w - 0.5, onSide0 ? u.z : u.z + ut.depth_ft);
                            return (
                              <line x1={tx(ex1)} y1={tz(ez1)} x2={tx(ex2)} y2={tz(ez2)}
                                    stroke="#ef4444" strokeWidth="2" />
                            );
                          })()}
                        </g>
                      );
                    })}

                    {/* Unit label + TYPE TAG (S / 1BR / 2BR) */}
                    <text x={tx(ucx)} y={tz(ucz) + 9}
                          textAnchor="middle" fill={unitColor} fontSize="4.5"
                          fontFamily="monospace" letterSpacing="0.5" fontWeight="700">
                      {unitLabel}
                    </text>
                    {(() => {
                      const TYPE_TAG = {
                        STUDIO_15: "S",
                        ONEBR_25: "1BR",
                        TWOBR_40: "2BR",
                        COMM_40: "COMM",
                      }[ut.id] || "";
                      if (!TYPE_TAG) return null;
                      // Position tag at the top of the unit so it doesn't overlap rooms
                      const [tcx, tcz] = transformWingPoint(wing, u.x + ut.length_ft / 2, u.z + 1.2);
                      return (
                        <g>
                          <rect
                            x={tx(tcx) - (ut.id === "TWOBR_40" ? 10 : 7)}
                            y={tz(tcz) - 4}
                            width={ut.id === "TWOBR_40" ? 20 : 14}
                            height="6"
                            rx="1.2"
                            fill={unitColor}
                            fillOpacity="0.95"
                          />
                          <text
                            x={tx(tcx)}
                            y={tz(tcz) + 0.5}
                            textAnchor="middle"
                            fill="#0b0f14"
                            fontSize="4.2"
                            fontFamily="monospace"
                            fontWeight="900"
                            letterSpacing="0.6"
                          >
                            {TYPE_TAG}
                          </text>
                        </g>
                      );
                    })()}

                    {/* Entry door + balcony marker on the catwalk side */}
                    {ut.doors[0] && (() => {
                      const balconyOnFront = wing.balcony_side === "front";
                      // Entry on CATWALK side = opposite of balcony
                      const catwalkZ = balconyOnFront ? u.z + ut.depth_ft : u.z;
                      const balconyZ = balconyOnFront ? u.z : u.z + ut.depth_ft;
                      const d = ut.doors[0];
                      const doorPos = d.pos;
                      const [dx1, dz1] = transformWingPoint(wing, u.x + doorPos - d.width / 2, catwalkZ);
                      const [dx2, dz2] = transformWingPoint(wing, u.x + doorPos + d.width / 2, catwalkZ);
                      const elems = [
                        <line key="door" x1={tx(dx1)} y1={tz(dz1)} x2={tx(dx2)} y2={tz(dz2)}
                              stroke={showEgress ? "#22c55e" : "#94a3b8"} strokeWidth="2.5" />
                      ];
                      // Balcony marker on top floors
                      if (showBalconies && activeFloor >= 2 && ut.id !== "COMM_40") {
                        const balDepth = 4;
                        const offDir = balconyOnFront ? -1 : 1;
                        const bp1 = transformWingPoint(wing, u.x + 1, balconyZ + offDir * balDepth);
                        const bp2 = transformWingPoint(wing, u.x + ut.length_ft - 1, balconyZ + offDir * balDepth);
                        const bp3 = transformWingPoint(wing, u.x + ut.length_ft - 1, balconyZ);
                        const bp4 = transformWingPoint(wing, u.x + 1, balconyZ);
                        const balPoly = [bp1, bp2, bp3, bp4].map(([wx, wz]) => `${tx(wx)},${tz(wz)}`).join(" ");
                        elems.push(
                          <polygon key="bal" points={balPoly} fill="#1e293b" fillOpacity="0.5"
                                   stroke="#94a3b8" strokeWidth="0.6" strokeDasharray="2 2" />
                        );
                      }
                      return <g key="door-bal">{elems}</g>;
                    })()}
                  </g>
                );
              })}

              {/* Wing label — hidden on site composite to avoid stacking overlap */}
              {building.kind !== "site_composite" && (
                <text x={tx(labelX)} y={tz(labelZ) - 18}
                      textAnchor="middle" fill="#facc15" fontSize="9"
                      letterSpacing="2" fontFamily="monospace" fontWeight="700"
                      opacity="0.7">
                  {wing.label.toUpperCase()}
                </text>
              )}
            </g>
          );
        })}

        {/* Courtyard / common note */}
        {building.kind === "U_residential" && (() => {
          const [cx, cz] = [building.width_ft / 2, 200];
          return (
            <text x={tx(cx)} y={tz(cz)} textAnchor="middle" fill="#5b6776"
                  fontSize="14" letterSpacing="8" fontFamily="monospace">
              COURTYARD
            </text>
          );
        })()}
      </svg>
    </div>
  );
}
